import type {Component, Editor} from 'grapesjs';
import {FORM_MODE_MODEL_KEY, FormMakerFormMode} from '@FormMakerReact/form-maker.types';
import {POSTERMYWALL_CONFIRMATION_FORM_TYPE} from '@FormMakerReact/components/postermywall-confirmation-form/postermywall-confirmation-form';
import {POSTERMYWALL_SIGNUP_FORM_TYPE} from '@FormMakerReact/components/postermywall-signup-form/postermywall-signup-form';
import {closeEmbeddedFormEditor, saveAndCloseEmbeddedFormEditor} from '@FormMakerReact/lib/form-maker-embedded-editor-library';
import {noop} from '@Utils/general.util';
import {STORE_OPTS_MODEL_KEY} from './form-maker-storage';

export const FORM_MAKER_SAVE_COMMAND = 'form-maker-save';
export const FORM_MAKER_DESELECT_COMMAND = 'form-maker-deselect';
export const FORM_MAKER_SELECT_NEXT_COMMAND = 'form-maker-select-next';
export const FORM_MAKER_SELECT_PREV_COMMAND = 'form-maker-select-prev';
export const FORM_MAKER_SAVE_AND_CLOSE_EMBEDDED_COMMAND = 'form-maker-save-and-close-embedded';
export const FORM_MAKER_CLOSE_EMBEDDED_COMMAND = 'form-maker-close-embedded';

export interface CloseEmbeddedFormCommandOpts {
  isSilentClose: boolean;
}

/** Registers the editor commands the keymaps dispatch — chiefly the save command, so Ctrl+S can store without React context access. */
export function importCommands(editor: Editor): void {
  editor.Commands.add(FORM_MAKER_SAVE_COMMAND, {
    run(): void {
      const opts = editor.getModel().get(STORE_OPTS_MODEL_KEY) as object | undefined;
      if (opts) {
        void editor.store(opts).catch(noop);
      }
    },
  });
  editor.Commands.add(FORM_MAKER_DESELECT_COMMAND, {
    run(ed: Editor): void {
      ed.select(undefined);
    },
  });
  editor.Commands.add(FORM_MAKER_SELECT_NEXT_COMMAND, {
    run(ed: Editor): void {
      selectByOffset(ed, 1);
    },
  });
  editor.Commands.add(FORM_MAKER_SELECT_PREV_COMMAND, {
    run(ed: Editor): void {
      selectByOffset(ed, -1);
    },
  });
  editor.Commands.add(FORM_MAKER_SAVE_AND_CLOSE_EMBEDDED_COMMAND, {
    run(ed: Editor): void {
      void saveAndCloseEmbeddedFormEditor(ed);
    },
  });
  editor.Commands.add(FORM_MAKER_CLOSE_EMBEDDED_COMMAND, {
    run(_ed: Editor, _sender: unknown, opts: CloseEmbeddedFormCommandOpts): void {
      closeEmbeddedFormEditor(opts.isSilentClose);
    },
  });
}

function selectByOffset(editor: Editor, offset: number): void {
  const selectable = collectSelectableInDocOrder(editor);
  if (selectable.length === 0) {
    return;
  }
  const current = editor.getSelected();
  const currentIdx = current ? selectable.indexOf(current) : -1;
  const len = selectable.length;
  const nextIdx = currentIdx === -1 ? (offset > 0 ? 0 : len - 1) : (currentIdx + offset + len) % len;
  editor.select(selectable[nextIdx]);
}

function collectSelectableInDocOrder(editor: Editor): Component[] {
  const root = getActiveFormRoot(editor);
  if (!root) {
    return [];
  }
  const out: Component[] = [];
  walk(root, (component) => {
    if (component.get('selectable') === false) {
      return;
    }
    if (isInsideHiddenSubtree(component, root)) {
      return;
    }
    out.push(component);
  });
  return out;
}

function isInsideHiddenSubtree(component: Component, root: Component): boolean {
  let current = component.parent();
  while (current && current !== root) {
    const style = current.getStyle() as Record<string, string | undefined>;
    if (style.display === 'none') {
      return true;
    }
    current = current.parent();
  }
  return false;
}

function getActiveFormRoot(editor: Editor): Component | undefined {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return undefined;
  }
  const mode = editor.getModel().get(FORM_MODE_MODEL_KEY) as FormMakerFormMode | undefined;
  const rootType = mode === FormMakerFormMode.CONFIRMATION ? POSTERMYWALL_CONFIRMATION_FORM_TYPE : POSTERMYWALL_SIGNUP_FORM_TYPE;
  return wrapper.findType(rootType)[0];
}

function walk(component: Component, visit: (c: Component) => void): void {
  visit(component);
  component.components().forEach((child) => {
    walk(child, visit);
  });
}
